// src/services/webSocketService.js
import {useAuthStore} from '@core/stores/authStore';
import EventBus from '@core/services/utils/appEventBusService';
import BusEnum from '@core/enums/BusEnum';
import appConsoleService from '@core/services/utils/appConsoleService';
import appEnvService from '@core/services/utils/appEnvService';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.reconnectAttempts = 0;
    this.reconnectDelay = 2000;      // délai initial
    this.maxReconnectDelay = 30000;  // plafond : on réessaie indéfiniment, au plus toutes les 30 s
    this.reconnectTimer = null;
    this.messageQueue = [];
    this.heartbeatInterval = null;
    this.wsId = null;
    this.closedByClient = false;
    this.installLifecycleHooks();
  }

  /**
   * Reconnexion immédiate quand le réseau revient ou que l'onglet redevient visible :
   * un backend redémarré ou une coupure réseau ne doivent jamais laisser la session
   * sans websocket (sinon plus aucune mise à jour temps réel, ex. kanban RMS).
   */
  installLifecycleHooks() {
    if (typeof window === 'undefined' || this.hooksInstalled) return;
    this.hooksInstalled = true;
    const wake = () => {
      if (this.closedByClient || this.connected) return;
      if (this.socket && this.socket.readyState === WebSocket.CONNECTING) return;
      this.reconnectAttempts = 0;
      this.scheduleReconnect(0);
    };
    window.addEventListener('online', wake);
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') wake(); });
    window.addEventListener('focus', wake);
  }

  // Singleton pattern
  static getInstance() {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  getCurrentWsId() {
    return this.wsId;
  }

  getUniqueID() {
    function s4() {
      return Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
    }
    return s4() + s4() + '-' + s4();
  }

  buildUrl() {
    // Connect to WebSocket
    const APP_PROTOCOL = appEnvService.getEnv("APP_PROTOCOL");
    const APP_DOMAIN = appEnvService.getEnv("APP_DOMAIN");

    let WS_URL = APP_PROTOCOL + "://" + APP_DOMAIN + "/wss/call";

    if (WS_URL.startsWith("https")) {
      WS_URL = WS_URL.replaceAll("https", "wss");
    } else {
      WS_URL = WS_URL.replaceAll("http", "ws");
    }

    this.wsId = this.getUniqueID();
    let fullUrl;

    const authStore = useAuthStore();
    if (authStore.isLoggedIn && authStore.currentUser) {
      fullUrl = `${WS_URL}?wsId=${this.wsId}&token=${authStore.currentUser.access_token}`;
    } else {
      fullUrl = `${WS_URL}?wsId=${this.wsId}&token=unregistered`;
    }
    return fullUrl;
  }

  connect() {
    let serverUrl = this.buildUrl();
    this.closedByClient = false;

    if (this.socket && this.connected) {
      return Promise.resolve(); // Already connected
    }
    if (this.socket && this.socket.readyState === WebSocket.CONNECTING) {
      return Promise.resolve(); // Connexion déjà en cours
    }

    return new Promise((resolve, reject) => {
      try {
        appConsoleService.debug('WebSocket', `Connecting to ${serverUrl}`);
        this.socket = new WebSocket(serverUrl);

        // Setup event listeners
        this.socket.onopen = () => {
          appConsoleService.debug('WebSocket', 'Connection established');
          this.connected = true;
          this.reconnectAttempts = 0;
          this.reconnectDelay = 2000;

          // Process any messages that were queued while disconnected
          this.flushMessageQueue();

          // Setup heartbeat interval to keep connection alive
          this.setupHeartbeat();

          // Send authentication message
          this.sendAuthMessage();

          resolve();
        };

        this.socket.onmessage = (event) => {
          this.handleMessage(event);
        };

        this.socket.onerror = (error) => {
          appConsoleService.error('WebSocket', 'Connection error:', error);
          if (!this.connected) {
            reject(error);
          }
        };

        this.socket.onclose = () => {
          appConsoleService.debug('WebSocket', 'Connection closed');
          this.connected = false;

          // Clear heartbeat interval
          if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
          }

          this.attemptReconnect();
        };
      } catch (error) {
        appConsoleService.error('WebSocket', 'Failed to create connection:', error);
        reject(error);
      }
    });
  }

  attemptReconnect() {
    if (this.closedByClient) return;

    // Backoff exponentiel plafonné + un peu d'aléa, sans limite de tentatives : le serveur
    // peut redémarrer plusieurs minutes, la session doit se raccrocher toute seule.
    this.reconnectAttempts++;
    const base = Math.min(this.maxReconnectDelay, this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts - 1));
    const delay = Math.round(base * (0.8 + Math.random() * 0.4));

    appConsoleService.debug('WebSocket', `Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);
    this.scheduleReconnect(delay);
  }

  scheduleReconnect(delay) {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect().catch(() => {
        // Échec : onclose déclenchera la tentative suivante
      });
    }, delay);
  }

  setupHeartbeat() {
    // Send a heartbeat every 20 seconds to keep connection alive
    this.heartbeatInterval = setInterval(() => {
      if (this.connected) {
        this.send({
          cmd: 'HEARTBEAT',
          wsId: this.wsId,
          timestamp: Date.now()
        });
      }
    }, 20000);
  }

  sendAuthMessage() {
    const authStore = useAuthStore();
    if (!authStore.isLoggedIn || !authStore.currentUser) {
      return;
    }

    this.send({
      cmd: 'AUTH',
      userId: authStore.currentUser.id,
      token: authStore.access_token,
      timestamp: Date.now()
    });
  }

  handleMessage(event) {
    try {
      const message = JSON.parse(event.data);

      // Handle system messages
      if (message.cmd === BusEnum.KEEP_ALIVE) {
        // Server response to our heartbeat
        appConsoleService.debug('WebSocket', 'Received keepalive');
        return;
      }

      if (message.cmd === BusEnum.CONNECTED) {
        appConsoleService.debug('WebSocket', `Received connection confirmation, wsId: ${message.wsId}`);
        return;
      }

      // Broadcasting
      EventBus.emit(message.cmd, message.payload);

    } catch (error) {
      appConsoleService.error('WebSocket', 'Error handling message:', error);
    }
  }

  send(message) {
    if (!this.connected) {
      // Queue message for later if not connected
      this.messageQueue.push(message);
      return false;
    }

    try {
      this.socket.send(JSON.stringify(message));
      return true;
    } catch (error) {
      appConsoleService.error('WebSocket', 'Error sending message:', error);
      return false;
    }
  }

  flushMessageQueue() {
    if (this.messageQueue.length === 0) return;

    appConsoleService.debug('WebSocket', `Sending ${this.messageQueue.length} queued messages`);

    // Send all queued messages
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      this.send(message);
    }
  }

  // Send a chat message
  sendChatMessage(message, chatId, receiverId = null) {
    const authStore = useAuthStore();

    return this.send({
      cmd: 'CHAT',
      token: authStore.currentUser?.access_token,
      data: {
        type: 'chat',
        senderId: authStore.currentUser?.id,
        receiverId,
        chatId,
        text: message.text,
        timestamp: Date.now()
      }
    });
  }

  // Send a typing indicator
  sendTypingEvent(chatId, receiverId = null) {
    const authStore = useAuthStore();

    return this.send({
      cmd: 'TYPING',
      token: authStore.currentUser?.access_token,
      data: {
        userId: authStore.currentUser?.id,
        receiverId,
        chatId,
        timestamp: Date.now()
      }
    });
  }

  // Initiate a video call
  initiateVideoCall(receiverId, chatId) {
    const authStore = useAuthStore();

    return this.send({
      cmd: 'CALL',
      token: authStore.currentUser?.access_token,
      data: {
        type: 'call_request',
        action: 'call_request',
        senderId: authStore.currentUser?.id,
        receiverId,
        chatId,
        timestamp: Date.now()
      }
    });
  }

  // Update call status (accept, decline, end)
  updateCallStatus(receiverId, chatId, action) {
    const authStore = useAuthStore();

    return this.send({
      cmd: 'CALL',
      token: authStore.currentUser?.access_token,
      data: {
        type: action,
        action: action,
        senderId: authStore.currentUser?.id,
        receiverId,
        chatId,
        timestamp: Date.now()
      }
    });
  }

  // Send RTC signaling data
  sendRtcSignal(streamType, action, data, receiverId, chatId) {
    try {
      const authStore = useAuthStore();
      const cmd = streamType === 'screen' ? BusEnum.SCREEN_SHARE : BusEnum.CALL;

      // Enhanced logging
      appConsoleService.debug('WebSocketService', `Sending ${streamType} signal: ${action}`, {
        from: authStore.currentUser?.id,
        to: receiverId,
        chatId: chatId,
        action: action
      });

      // If this is an offer or answer, log essential SDP info
      if (action === 'offer' || action === 'answer') {
        appConsoleService.debug('WebSocketService', `${action.toUpperCase()} SDP type: ${data.type}`);
      }

      // Build and send the message
      const result = this.send({
        cmd: cmd,
        token: authStore.currentUser?.access_token,
        data: {
          type: streamType === 'screen' ? 'screen_share' : 'video',
          action,
          senderId: authStore.currentUser?.id,
          receiverId,
          chatId,
          sdp: action === 'offer' || action === 'answer' ? data : undefined,
          candidate: action === 'ice_candidate' ? data : undefined,
          timestamp: Date.now()
        }
      });

      if (result) {
        appConsoleService.debug('WebSocketService', `Successfully sent ${action} signal`);
      } else {
        appConsoleService.warn('WebSocketService', `Failed to send ${action} signal - message queued`);
      }

      return result;
    } catch (error) {
      appConsoleService.error('WebSocketService', `Error sending RTC signal (${action}): ${error.message}`, error);
      return false;
    }
  }

  // Start screen sharing
  startScreenShare(receiverId, chatId) {
    const authStore = useAuthStore();

    return this.send({
      cmd: BusEnum.SCREEN_SHARE,
      token: authStore.currentUser?.access_token,
      data: {
        type: 'screen_share',
        action: 'start',
        senderId: authStore.currentUser?.id,
        receiverId,
        chatId,
        timestamp: Date.now()
      }
    });
  }

  // Stop screen sharing
  stopScreenShare(receiverId, chatId) {
    const authStore = useAuthStore();

    return this.send({
      cmd: BusEnum.SCREEN_SHARE,
      token: authStore.currentUser?.access_token,
      data: {
        type: 'screen_share',
        action: 'stop',
        senderId: authStore.currentUser?.id,
        receiverId,
        chatId,
        timestamp: Date.now()
      }
    });
  }

  // Close the WebSocket connection (fermeture voulue : pas de reconnexion automatique)
  disconnect() {
    this.closedByClient = true;
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this.connected = false;
    this.wsId = null;

    // Clear reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Clear heartbeat interval
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }


  /////////////


  // Replace these methods in your WebSocketService.js

  /**
   * Send a meeting invitation to another user
   * @param {string} receiverId - ID of the recipient
   * @param {string} chatId - Chat ID context
   * @param {string} roomName - Jitsi room name
   * @param {string} domain - Jitsi server domain
   * @param {string} senderName - Name of the user sending the invitation
   * @returns {boolean} - Whether the operation was successful
   */
  sendMeetingInvitation(receiverId, chatId, roomName, domain, senderName) {
    const authStore = useAuthStore();

    return this.send({
      cmd: 'JITSI_MEETING',
      token: authStore.currentUser?.access_token,
      data: {
        type: 'jitsi_meeting',
        action: 'invitation',
        senderId: authStore.currentUser?.id,
        receiverId,
        chatId,
        roomName,
        domain,
        senderName,
        timestamp: Date.now()
      }
    });
  }

  /**
   * Send a meeting end notification
   * @param {string} receiverId - ID of the recipient
   * @param {string} chatId - Chat ID context
   * @param {string} roomName - Jitsi room name
   * @returns {boolean} - Whether the operation was successful
   */
  sendMeetingEnd(receiverId, chatId, roomName) {
    const authStore = useAuthStore();

    return this.send({
      cmd: 'JITSI_MEETING',
      token: authStore.currentUser?.access_token,
      data: {
        type: 'jitsi_meeting',
        action: 'ended',
        senderId: authStore.currentUser?.id,
        receiverId,
        chatId,
        roomName,
        timestamp: Date.now()
      }
    });
  }


}

export default WebSocketService;
